import { useEffect, useRef, useState } from "react";

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
const STORAGE_KEY = "mm_last_activity";
const POLL_MS = 2000;

/**
 * Records activity from outside the normal window-event listeners below —
 * for flows where the user is genuinely present and engaged but the
 * standard events can't see it. The one concrete case in this app:
 * Stripe's CardElement (FundWalletModal) renders inside a cross-origin
 * iframe, so keystrokes typed into the card fields never reach this
 * window's own keydown/mousemove listeners at all. A careful card entry
 * that takes a few minutes must never be interrupted by an idle logout —
 * see useKeepIdleLogoutAlive below, which calls this periodically while
 * such a flow is in progress.
 */
export function touchIdleActivity() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // Storage blocked — falls back to this tab's own listeners only.
  }
}

/**
 * Call with `active=true` for the duration of a flow that must never be
 * interrupted by the idle-logout warning (e.g. an open payment modal),
 * even though the user's input isn't visible to this window's own
 * activity listeners (Stripe's CardElement iframe).
 */
export function useKeepIdleLogoutAlive(active) {
  useEffect(() => {
    if (!active) return undefined;
    touchIdleActivity();
    const interval = setInterval(touchIdleActivity, 20000);
    return () => clearInterval(interval);
  }, [active]);
}

/**
 * Idle-timeout tracker for a financial app: after `warningAfterMs` of no
 * mouse/keyboard/scroll/touch activity, `showWarning` becomes true; if
 * still idle `countdownMs` after that, `onTimeout()` fires.
 *
 * Activity is tracked via a shared localStorage timestamp rather than a
 * plain in-tab timer, so it's cross-tab aware — actively typing in one
 * tab keeps every other open tab's session alive too, instead of one
 * idle tab silently logging the whole browser out from under an active
 * one. If storage is unavailable (private browsing, blocked), tracking
 * degrades to "never idle" for this tab rather than "always idle" — a
 * broken timer should not itself become a way to force logouts.
 */
export function useIdleLogout({
  warningAfterMs,
  countdownMs,
  onTimeout,
  enabled = true,
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(countdownMs / 1000));
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  useEffect(() => {
    if (!enabled) return undefined;
    touchIdleActivity();

    let lastMarked = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastMarked < 1000) return; // throttle writes
      lastMarked = now;
      touchIdleActivity();
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    const interval = setInterval(() => {
      let last = Date.now();
      let storageReadable = true;
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) last = Number(raw);
      } catch {
        storageReadable = false;
      }
      if (!storageReadable) return; // never idle if we can't read the clock

      const idleFor = Date.now() - last;
      const totalMs = warningAfterMs + countdownMs;
      if (idleFor >= totalMs) {
        clearInterval(interval); // stop polling immediately -- don't
                                   // keep firing onTimeout every POLL_MS
                                   // until the parent re-renders with
                                   // enabled=false
        setShowWarning(false);
        onTimeoutRef.current?.();
      } else if (idleFor >= warningAfterMs) {
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((totalMs - idleFor) / 1000)));
      } else {
        setShowWarning(false);
      }
    }, POLL_MS);

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [enabled, warningAfterMs, countdownMs]);

  const stayLoggedIn = () => {
    touchIdleActivity();
    setShowWarning(false);
  };

  return { showWarning, secondsLeft, stayLoggedIn };
}
